// /profile/rabin.jsx
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import avatar from "./images/profile-pic.jpeg";

const EMAIL = "er.rabinpantha@gmail.com";
const PHONE = "641-451-3463";
const LOCATION = "Herndon, Virginia, USA";

const LINKS = {
  linkedin: "https://www.linkedin.com/in/rabinpantha/",
  github: "https://github.com/rbnpantha",
  // Optional: host a PDF and point here
  resume: "/resumes/resume_rabin-pantha.pdf",
};

const TAGLINE = "Senior Software Engineer • Gen AI Developer • 10+ years";

const ABOUT =
  "Senior software engineer specializing in scalable Java services, microservices, and cloud-native platforms. I build resilient, observable systems and apply GenAI to boost content workflows, personalization, and developer productivity.";

const SKILL_GROUPS = [
  {
    label: "Languages",
    items: ["Java (7–21)", "Python", "JavaScript", "TypeScript"],
  },
  {
    label: "Frameworks",
    items: [
      "Spring Boot",
      "Micronaut",
      "Hibernate",
      "Angular",
      "React",
    ],
  },
  {
    label: "Data",
    items: [
      "MySQL",
      "PostgreSQL",
      "Oracle",
      "MongoDB",
      "Cassandra",
      "Cosmos",
      "DynamoDB",
      "Elasticsearch",
      "Redis",
      "Kafka",
    ],
  },
  {
    label: "Cloud & DevOps",
    items: [
      "AWS (EKS, Lambda, S3, CloudFront, SageMaker, Polly)",
      "Kubernetes",
      "Docker",
      "Terraform",
      "GitLab CI/CD",
      "Jenkins",
      "Bamboo",
      "Service Bus",
    ],
  },
  {
    label: "Observability",
    items: ["NewRelic", "Datadog", "Kibana", "Synthetic Monitoring"],
  },
  {
    label: "Testing & Quality",
    items: ["JUnit 4/5", "Mockito", "JMeter", "SonarQube"],
  },
  {
    label: "Security / Auth",
    items: ["OAuth2", "OIDC", "SAML", "WAF/TLS (edge)"],
  },
  {
    label: "AI",
    items: ["OpenAI/ChatGPT", "Generative AI", "Windsurf", "Github Copilot"],
  },
];

const EXPERIENCE = [
  {
    company: "Capital One",
    location: "Mclean, Virginia",
    period: "Mar 2025 – Present",
    role: "Senior Software Development Engineer",
    bullets: [
    "Enabled hundreds of millions in annual fraud-loss recoveries by automating and hardening high-volume recovery orchestration (Java, Spring Boot, Python, AWS Step Functions/Lambda, DynamoDB)",
    "Reduced net fraud losses by $200M+ (annualized estimate) by streamlining dispute decisioning and eliminating lifecycle delays through event-driven services (Kafka Streams, decision/rules services)",
    "Owned and scaled backend services for platform supporting 10K+ agents and ~15M transactions/year, improving resiliency with idempotency, retries, and SLO-aligned monitoring (Java 21, Spring Boot, ECS/ALB, PostgreSQL)",
    "Delivered 3 production microservices and multiple serverless workflows; reduced dev/review effort ~20-30% and lowered QA regressions by closing test gaps and accelerating refactors using GitHub Copilot and Windsurf",
    "Processed 3M+ claims/year with near-real-time state transitions by implementing event-driven orchestration and ensuring consistent downstream updates (Kafka Streams, message-driven design)",
    "Partnered with product, QA, and frontend teams to refine agent claim-filing APIs and improve end-to-end performance and usability (REST API design, React collaboration)",
    "Led design reviews and production-readiness practices (runbooks, alarms, load tests) to improve peak-load throughput stability (JMeter, JVM tuning, autoscaling)",
  
    ],
  },
  {
    company: "Sinclair Digital",
    location: "Seattle, Washington",
    period: "Mar 2022 – Feb 2025",
    role: "Senior Software Development Engineer",
    bullets: [
      "Reduced infrastructure costs by 10% by optimizing cloud resources, removing 600GB of unused assets, and cutting read-only instances by 50% while improving content delivery performance via proper CloudFront CDN configuration (AWS, CloudFront, EC2, S3)",
      "Increased organic traffic by 12% in 6 months by building AI-powered SEO automation that generated dynamic titles/summaries and auto-tagged news content using OpenAI integrations (Java/Spring Boot, OpenAI APIs, SEO automation)",
      "Migrated 4.5M video assets (~950TB) from Edgio to AWS by building high-throughput migration tooling on Spot EC2 and ensuring seamless database reference updates to prevent broken playback paths (Python/Java scripts, AWS EC2 Spot, S3, DynamoDB)",
      "Cut image storage costs by ~42% in 6 months by implementing S3 Lifecycle policies to move infrequently accessed media to Amazon Glacier, balancing retention needs with storage optimization (S3 Lifecycle, Glacier, cost governance)",
      "Achieved 18% user adoption of a “Story-to-Speech” capability by implementing a scalable, serverless audio pipeline using Amazon Polly + SQS + Lambda + Kafka, enabling automated content-to-audio delivery (serverless, event-driven workflows)",
      "Improved scalability and deployment flexibility by designing and deploying containerized microservices on AWS EKS with Docker/Kubernetes, enabling consistent environments and faster rollouts (Spring Boot, Kubernetes, GitLab CI/CD)",
      "Reduced processing time and increased reliability by implementing an event-driven architecture to decouple monolith dependencies and improve asynchronous processing paths (Kafka, async patterns, distributed systems)",
      "Accelerated real-time message processing by optimizing Kafka configuration and integrating event flows with Python AWS Lambda, improving end-to-end responsiveness for content workflows (Kafka tuning, Lambda, Python)",
      "Improved incident detection and response time by implementing synthetic monitoring with automated notifications and PagerDuty escalation for customer-impacting failures (New Relic/Synthetics, alerting, on-call readiness)"

    ],
  },
  {
    company: "USAA",
    location: "San Antonio, TX (Remote)",
    period: "Oct 2021 – Mar 2022",
    role: "Senior Software Engineer",
    bullets: [
      "Reduced unauthorized/high-risk debit-card spend exposure by building and scaling backend controls that enforced daily/weekly/monthly transaction-count and dollar-amount limits, preventing out-of-policy authorizations in real time (Java, Spring, REST, DB2/Oracle)",
      "Improved authorization accuracy and consistency by implementing centralized limit-validation logic used across channels (ATM/POS/e-commerce), ensuring deterministic decisioning for transaction attempts (Spring services, rules/validation engine patterns)",
      "Increased system resiliency and decision latency stability by optimizing limit-check flows (efficient reads, caching patterns where applicable, and lean payload design) to support peak transaction volume without degraded performance (Java, performance tuning, data access optimization)",
      "Improved data pipeline reliability for limit/reference data by integrating enterprise ETL workflows and ensuring clean, timely movement of configuration and customer/account attributes into authorization services (IBM DataStage, DB2/Oracle, data quality checks)",
      "Accelerated incident triage and reduced time-to-diagnosis by implementing actionable monitoring/alerts and searchable logs for transaction-limit decisions, enabling proactive detection of spikes, failures, and misconfigurations (Datadog, Elasticsearch, Kibana)",
      "Reduced regression risk in production releases by leading code reviews and design assessments for changes impacting authorization/controls, strengthening correctness for high-stakes financial decisioning (Agile, GitLab, Jira, engineering governance)",
      "Improved delivery speed and engineering consistency by mentoring teammates on testing strategies (unit/contract), clean code practices, and operational readiness standards (team leadership, CI/CD mindset)"
    ],
  },
  {
    company: "Walgreens (WBA)",
    location: "Deerfield, IL, USA",
    period: "Jun 2017 – Oct 2021",
    role: "Senior Software Engineer • Team Lead",
    bullets: [
      "Improved pharmacy platform performance by 15% and reduced database load by 25% by replacing Ehcache with Redis and redesigning caching for high-read workflows (patient/profile, drug lookup, eligibility/plan, fulfillment status) (Java, Spring, Redis, SQL)",
      "Modernized a 25-year-old pharmacy monolith into a fault-tolerant, event-driven microservices architecture to reduce deployment coupling and improve resilience across critical pharmacy workflows (Spring, Kafka, microservices, distributed design)",
      "Increased scalability for high-concurrency pharmacy traffic by developing reactive, non-blocking services with Spring WebFlux, enabling more concurrent requests with lower thread/resource overhead (WebFlux, reactive programming)",
      "Improved reliability of prescription workflow processing by decoupling synchronous dependencies using Kafka-driven events (status updates, notifications, downstream integrations), minimizing failure blast radius (Kafka, event-driven architecture, retry/idempotency patterns)",
      "Enabled high-throughput, distributed data access by introducing Cassandra for write-heavy/large-scale workflows and designing partition-friendly models for pharmacy transaction patterns (Cassandra, distributed data modeling)",
      "Reduced production risk during modernization by running architecture reviews, enforcing design patterns, and leading high-quality peer reviews for changes impacting patient/pharmacy operations (governance, code reviews, design assessments)",
      "Improved operational visibility and faster troubleshooting for distributed pharmacy services by strengthening dashboards/log search and promoting “detect early, recover fast” practices (Kibana/observability, incident readiness)",
      "Accelerated release execution and environment consistency by standardizing containerized deployments and aligning CI/CD practices across services (Docker, Kubernetes, Jenkins/Bamboo/Spinnaker)",
      "Delivered new pharmacy features while migrating legacy components safely using incremental modernization patterns (backward compatibility + phased cutovers) to keep pharmacy operations stable throughout the transition (Shadow-mode and canary deployment,  migration, safe rollout)"

    ],
  },
  {
    company: "Ishani Technology",
    location: "Lalitpur, Nepal",
    period: "Jan 2015 – Jul 2016",
    role: "Software Engineer",
    bullets: [
      "Improved registration throughput and reduced approval-cycle delays by digitizing end-to-end company registration + multi-level approval workflows across government authorities, replacing manual handoffs with auditable, role-based processing (Java, Spring, PostgreSQL)",
      "Reduced rework and invalid submissions by implementing server-side validation and rule checks for registration forms and supporting documents before submissions entered the approval queue (Spring, validation patterns, API design)",
      "Strengthened security and compliance for government workflows by enforcing authentication/authorization and fine-grained role-based access controls for multi-authority approvals (Spring Security, LDAP, RBAC)",
      "Enabled cross-department interoperability by designing secure REST APIs for data sharing and integrations with external government systems, ensuring consistent data exchange and traceability (REST, Apigee, API governance)",
      "Improved release reliability and environment consistency by containerizing services and managing staging→production deployments using CI/CD and orchestration tooling (Docker, GitLab, Kubernetes)",
      "Reduced defects and improved maintainability by applying TDD practices, increasing automated test coverage, and promoting clean code via peer reviews (unit testing, TDD, code reviews)",
      "Accelerated feature delivery and improved usability by collaborating with product/UX to translate policy requirements into user-friendly flows for web and Android applications (cross-functional delivery, REST APIs)",
      "Improved operational support and troubleshooting by implementing structured logging and clear error responses for validation/approval steps, making issues easier to trace across approval stages (observability basics, API error handling)"

    ],
  },
];

const EDUCATION = [
  {
    school: "Maharishi University of Management",
    degree: "M.S., Computer Science",
    year: "2017",
    location: "Fairfield, Iowa, USA",
  },
  {
    school: "Cosmos College of Management & Technology",
    degree: "B.E., Information & Technology",
    year: "2014",
    location: "Kathmandu, Nepal",
  },
];

const AWARDS = [
  "Above the Line Excellence Award — Sinclair Broadcasting Group (2023)",
  "Best Team Player (Team Energizer) — Walgreens/GlobalLogic (2018)",
  "Graduate Teaching Assistant — MUM (2016)",
  "Employee of the Month — Ishani Technology (Jun 2015, Mar 2016)",
];

function Pill({ children }) {
  return (
    <span className="badge bg-light text-muted border me-1 mb-1">{children}</span>
  );
}

function CopyButton({ text, label = "Copy" }) {
  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          // Tiny toast stand-in
          alert(`${label} copied: ${text}`);
        } catch {}
      }}
    >
      📋 {label}
    </button>
  );
}

export default function RabinProfile() {
  // SEO: page title + Person structured data
  useEffect(() => {
    document.title = "Rabin Pantha — Profile";
  }, []);

  const personJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Rabin Pantha",
      jobTitle: "Senior Software Engineer",
      worksFor: { "@type": "Organization", name: "Sinclair Digital" },
      url: LINKS.linkedin,
      sameAs: [LINKS.linkedin, LINKS.github],
      email: `mailto:${EMAIL}`,
      telephone: PHONE,
      address: {
        "@type": "PostalAddress",
        addressLocality: "Herndon",
        addressRegion: "VA",
        addressCountry: "US",
      },
      knowsAbout: SKILL_GROUPS.flatMap((g) => g.items),
    }),
    []
  );

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: Profile summary / quick nav */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card shadow-sm border-0 text-center mb-3">
              <div className="card-body">
                <img
                  src={avatar}
                  alt="Rabin Pantha"
                  className="rounded-circle mb-2"
                  width="96"
                  height="96"
                />
                <h1 className="h6 mb-1">Rabin Pantha</h1>
                <p className="small text-muted mb-2">{TAGLINE}</p>
                <div className="d-grid gap-2">
                  <a
                    className="btn btn-primary btn-sm"
                    href={LINKS.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn
                  </a>
                  <a
                    className="btn btn-outline-dark btn-sm"
                    href={LINKS.github}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GitHub
                  </a>
                  {LINKS.resume && (
                    <a
                      className="btn btn-outline-secondary btn-sm"
                      href={LINKS.resume}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download Resume
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h6 text-uppercase text-muted mb-2">
                  On this page
                </h2>
                <nav className="nav flex-column small">
                  <a className="nav-link px-0" href="#about">
                    About
                  </a>
                  <a className="nav-link px-0" href="#skills">
                    Skills
                  </a>
                  <a className="nav-link px-0" href="#experience">
                    Experience
                  </a>
                  <a className="nav-link px-0" href="#awards">
                    Awards
                  </a>
                  <a className="nav-link px-0" href="#education">
                    Education
                  </a>
                  <a className="nav-link px-0" href="#contact">
                    Contact
                  </a>
                </nav>
              </div>
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          {/* Hero */}
          <header className="mb-3">
            <div className="d-flex align-items-center gap-3">
              <img
                src={avatar}
                alt="Rabin Pantha"
                className="rounded-circle d-xl-none"
                width="64"
                height="64"
              />
              <div>
                <h1 className="h3 mb-1">Rabin Pantha</h1>
                <div className="text-muted">{TAGLINE}</div>
              </div>
            </div>
          </header>

          {/* About */}
          <section id="about" className="mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h5 mb-2">About</h2>
                <p className="mb-0">{ABOUT}</p>
              </div>
            </div>
          </section>

          {/* Key facts */}
          <section className="mb-4">
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="text-muted small">Location</div>
                    <div className="fw-semibold">{LOCATION}</div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="text-muted small">Email</div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                      <CopyButton text={EMAIL} label="Email" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="text-muted small">Phone</div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <a href={`tel:${PHONE.replace(/[^0-9]/g, "")}`}>{PHONE}</a>
                      <CopyButton text={PHONE} label="Phone" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Skills */}
          <section id="skills" className="mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h5 mb-3">Skills</h2>
                <div className="row g-3">
                  {SKILL_GROUPS.map((g) => (
                    <div className="col-12 col-md-6" key={g.label}>
                      <div className="mb-1 fw-semibold">{g.label}</div>
                      <div>
                        {g.items.map((i) => (
                          <Pill key={i}>{i}</Pill>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Experience */}
          <section id="experience" className="mb-4">
            <h2 className="h5 mb-3">Experience</h2>
            <div className="timeline">
              {EXPERIENCE.map((job) => (
                <div className="card shadow-sm border-0 mb-3" key={job.company}>
                  <div className="card-body">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <div>
                        <div className="fw-semibold">
                          {job.role} — {job.company}
                        </div>
                        <div className="text-muted small">
                          {job.location} • {job.period}
                        </div>
                      </div>
                    </div>
                    <ul className="mt-2 mb-0">
                      {job.bullets.map((b, i) => (
                        <li key={i} className="mb-1">
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Awards */}
          <section id="awards" className="mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h5 mb-2">Awards & Honors</h2>
                <ul className="mb-0">
                  {AWARDS.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Education */}
          <section id="education" className="mb-5">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h5 mb-2">Education</h2>
                <ul className="mb-0">
                  {EDUCATION.map((e) => (
                    <li key={e.school} className="mb-1">
                      <span className="fw-semibold">{e.degree}</span>, {e.school} ({e.year}) — {e.location}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Contact CTA */}
          <section id="contact" className="mb-5">
            <div className="card shadow-sm border-0">
              <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <h2 className="h5 mb-1">Let’s build something great</h2>
                  <div className="text-muted">
                    Open to senior engineering roles & AI platform work.
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <a className="btn btn-primary" href={`mailto:${EMAIL}`}>
                    Contact Me
                  </a>
                  <Link className="btn btn-outline-secondary" to="/projects">
                    See Projects
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* JSON-LD for SEO */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(personJsonLd) }}
          />
        </main>

        {/* RIGHT: optional spacer / future widgets */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h2 className="h6 text-uppercase text-muted mb-2">Featured Links</h2>
              <ul className="list-unstyled mb-0">
                <li className="mb-1">
                  <a href={LINKS.linkedin} target="_blank" rel="noopener noreferrer">
                    LinkedIn Profile ↗
                  </a>
                </li>
                <li className="mb-1">
                  <a href={LINKS.github} target="_blank" rel="noopener noreferrer">
                    GitHub Profile ↗
                  </a>
                </li>
                {LINKS.resume && (
                  <li className="mb-1">
                    <a href={LINKS.resume} target="_blank" rel="noopener noreferrer">
                      Download Resume (PDF) ↗
                    </a>
                  </li>
                )}
              </ul>
              <hr />
              <div className="small text-muted">
                Images are placeholders—swap in your headshot and any portfolio screenshots.
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
